import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, withCtx as _withCtx, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, resolveComponent as _resolveComponent, createElementVNode as _createElementVNode, isRef as _isRef, vModelCheckbox as _vModelCheckbox, withDirectives as _withDirectives, normalizeClass as _normalizeClass, renderList as _renderList, Fragment as _Fragment, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "today-dashboard" }
const _hoisted_2 = {
  key: 0,
  class: "picker-row"
}
const _hoisted_3 = { class: "panel panel--planning" }
const _hoisted_4 = { class: "panel__nav" }
const _hoisted_5 = { class: "panel panel--activities" }
const _hoisted_6 = { class: "panel__nav" }
const _hoisted_7 = { class: "panel panel--projects" }
const _hoisted_8 = { class: "budget-toggles" }
const _hoisted_9 = { class: "budget-toggle budget-toggle--favorites" }
const _hoisted_10 = { class: "budget-toggle" }
const _hoisted_11 = { class: "budget-toggle" }
const _hoisted_12 = { class: "budget-column-header" }
const _hoisted_13 = { class: "budget-column-label" }
const _hoisted_14 = { class: "budget-order-btns" }
const _hoisted_15 = ["title"]
const _hoisted_16 = ["title"]
const _hoisted_17 = { class: "budget-column-label" }
const _hoisted_18 = { class: "budget-order-btns" }
const _hoisted_19 = ["title"]
const _hoisted_20 = ["title"]
const _hoisted_21 = { class: "budget-column-label" }
const _hoisted_22 = { class: "budget-order-btns" }
const _hoisted_23 = ["title"]
const _hoisted_24 = ["title"]
const _hoisted_25 = { key: 0 }

import { computed, ref } from "vue";
import { useLocalStorage } from "@vueuse/core";
import { useI18n } from "vue-i18n";
import { useMutation, useQuery } from "@vue/apollo-composable";
import { useRouter } from "vue-router";
import { formatPrice } from "@/helpers/utils";

import enUK from "@/locales/home/en-UK.json";

import HeaderBlock from "@/components/HeaderBlock.vue";
import TileDashboard from "@/components/TileDashboard.vue";
import ProjectItem from "@/components/ProjectItem.vue";
import PlanningCalendar from "@/views/planning/PlanningCalendar.vue";
import PlanningModal from "@/views/planning/PlanningModal.vue";
import EditTimesheet from "@/components/CRUXEntity/EditTimesheet.vue";
import ActivityFeed from "@/views/ActivityFeed.vue";
import Multiselect from "@vueform/multiselect/src/Multiselect";

import { GET_USER } from "@/graphql/user/queries";
import { isAdmin } from "@/helpers/router/queries";
import { GET_EMPLOYEE_MONTHLY_PROJECTS } from "@/graphql/project/queries";
import { UserResponse } from "@/helpers/user/typesDefinition";
import {
  BudgetTracking,
  MonthlyProjectResult,
} from "@/helpers/project/typesDefinition";
import { UPDATE_FAVORITE } from "@/graphql/project/mutations";
import { logErrorMessages } from "@vue/apollo-util";
import { useToast } from "vue-toastification";
import { useTodayPlanning } from "@/composables/planning/useTodayPlanning";
import { usePlanningModals } from "@/composables/planning/usePlanningModals";
import { Icon } from "@iconify/vue";

type MessageSchema = typeof enUK;

type OrderDirection = "asc" | "desc" | null;

export default /*@__PURE__*/_defineComponent({
  __name: 'EmployeeHome',
  setup(__props) {

const router = useRouter();

const { t } = useI18n<[MessageSchema], "en-UK">({
  useScope: "global",
  inheritLocale: true,
  messages: {
    "en-UK": enUK,
  },
});

// --- Project filter (multi-select) ---
const selectedProjectIds = ref<string[]>([]);

// --- Budget visibility toggles (persisted in localStorage) ---
const showFavoritesOnly = useLocalStorage(
  "employeeHome-showFavoritesOnly",
  false,
);
const showTeamBudget = useLocalStorage("employeeHome-showTeamBudget", true);
const showPersonalBudget = useLocalStorage(
  "employeeHome-showPersonalBudget",
  true,
);

// --- Budget ordering ---
const nameOrder = ref<OrderDirection>("asc");
const teamOrder = ref<OrderDirection>(null);
const personalOrder = ref<OrderDirection>(null);

const setNameOrder = (direction: OrderDirection) => {
  nameOrder.value = nameOrder.value === direction ? null : direction;
  // Reset other orders when name order is set
  if (nameOrder.value) {
    teamOrder.value = null;
    personalOrder.value = null;
  }
};

const setTeamOrder = (direction: OrderDirection) => {
  teamOrder.value = teamOrder.value === direction ? null : direction;
  // Reset other orders when team order is set
  if (teamOrder.value) {
    nameOrder.value = null;
    personalOrder.value = null;
  }
};

const setPersonalOrder = (direction: OrderDirection) => {
  personalOrder.value = personalOrder.value === direction ? null : direction;
  // Reset other orders when personal order is set
  if (personalOrder.value) {
    nameOrder.value = null;
    teamOrder.value = null;
  }
};

// --- Today's Planning (left panel) ---
const planning = useTodayPlanning({
  projectIds: computed(() => selectedProjectIds.value),
});

// --- Planning Modals ---
const modals = usePlanningModals({
  projects: () => planning.projects.value,
  users: () => planning.users.value,
  onSaved: () => planning.refetch(),
});

// --- User & Projects (right panel) ---
const { result: userResult } = useQuery<UserResponse>(GET_USER);
const user = computed(() => {
  return userResult.value?.user;
});
const userName = computed(() => {
  return user.value?.firstName || "";
});
const isUserAdmin = computed(() => isAdmin(user.value?.accessRights));

const { result: projectsResult } = useQuery<{
  myMonthlyProjects: MonthlyProjectResult[];
}>(GET_EMPLOYEE_MONTHLY_PROJECTS);

const monthlyProjects = computed(() => {
  return projectsResult.value?.myMonthlyProjects?.map((monthlyProject) => {
    const orderedAmount = projectBudget(monthlyProject);
    const performedAmount = totalSpent(monthlyProject);

    const percentage = orderedAmount
      ? performedAmount / orderedAmount
      : performedAmount
      ? 100
      : 0;

    return {
      ...monthlyProject,
      percentage,
      period: monthlyProject.workingPeriod,
      performedAmount,
      orderedAmount,
    };
  });
});

const goToProject = (id: string) => {
  router.push({ name: "EmployeeProjectDetails", params: { id } });
};

const sortedProjects = computed(() => {
  let projects = [...(monthlyProjects.value ?? [])];

  // Filter by selected project IDs (show all if none selected)
  if (selectedProjectIds.value.length > 0) {
    projects = projects.filter((p) => selectedProjectIds.value.includes(p.id));
  }

  // Filter to favorites only
  if (showFavoritesOnly.value) {
    projects = projects.filter((p) => p.isFavorite);
  }

  // Apply ordering based on name, team, or personal budget
  if (nameOrder.value) {
    const multiplier = nameOrder.value === "asc" ? 1 : -1;

    projects = projects.sort((p1, p2) => {
      // Favorites first
      const isP1Favorite = p1.isFavorite;
      const isP2Favorite = p2.isFavorite;
      if (isP1Favorite && !isP2Favorite) return -1;
      if (!isP1Favorite && isP2Favorite) return 1;

      // Then alphabetically by project name
      return p1.name.localeCompare(p2.name) * multiplier;
    });
  } else if (teamOrder.value || personalOrder.value) {
    const activeOrder = teamOrder.value || personalOrder.value;
    const isTeamOrder = teamOrder.value !== null;
    const multiplier = activeOrder === "asc" ? 1 : -1;

    projects = projects.sort((p1, p2) => {
      // By budget percentage (consume / budget)
      // Treat zero/missing budget as -Infinity so it sorts to the bottom
      const p1Value = isTeamOrder
        ? p1.orderedAmount
          ? p1.percentage
          : -Infinity
        : p1.employeeBudgetAllocation?.allocatedAmount
        ? (p1.employeeBudgetAllocation.timesheetAmount ?? 0) /
          p1.employeeBudgetAllocation.allocatedAmount
        : -Infinity;
      const p2Value = isTeamOrder
        ? p2.orderedAmount
          ? p2.percentage
          : -Infinity
        : p2.employeeBudgetAllocation?.allocatedAmount
        ? (p2.employeeBudgetAllocation.timesheetAmount ?? 0) /
          p2.employeeBudgetAllocation.allocatedAmount
        : -Infinity;

      return (p1Value - p2Value) * multiplier;
    });
  } else {
    // Default sort: favorites first, then alphabetical
    projects = projects.sort((p1, p2) => {
      const isP1Favorite = p1.isFavorite;
      const isP2Favorite = p2.isFavorite;
      if (isP1Favorite && !isP2Favorite) return -1;
      if (!isP1Favorite && isP2Favorite) return 1;
      return p1.name.localeCompare(p2.name);
    });
  }

  return projects;
});

// --- Project filter options for Multiselect ---
const projectOptions = computed(() => {
  return (monthlyProjects.value ?? []).map((p) => ({
    value: p.id,
    label: p.name,
  }));
});

const { mutate: updateFavorite, onError: onErrorUpdateFavorite } = useMutation(
  UPDATE_FAVORITE,
  { refetchQueries: [{ query: GET_EMPLOYEE_MONTHLY_PROJECTS }] },
);

onErrorUpdateFavorite((error) => {
  logErrorMessages(error);
  useToast().error(
    t("errors.updateFavorite", { message: t("errors.internal") }),
  );
});

const toggleFavorite = (projectId: string) => {
  const project = monthlyProjects.value?.find((p) => p.id === projectId);
  updateFavorite({
    projectId: projectId,
    value: !(project?.isFavorite ?? false),
  });
};

function projectBudget(project: MonthlyProjectResult): number {
  if (project.budgetTracking === BudgetTracking.MONTHLY) {
    return project.teamMonthlyBudget?.allocatedAmount ?? 0;
  }
  return project.budget?.amount ?? 0;
}

function totalSpent(project: MonthlyProjectResult): number {
  if (project.budgetTracking === BudgetTracking.MONTHLY) {
    return project.teamMonthlyBudget?.timesheetAmount ?? 0;
  }
  return project.performedAmount ?? 0;
}

return (_ctx: any,_cache: any) => {
  const _component_router_link = _resolveComponent("router-link")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(HeaderBlock, {
      class: "header-home",
      title: _unref(t)('titles.myDashboard')
    }, {
      default: _withCtx(() => [
        _createTextVNode(_toDisplayString(_unref(t)("welcome", { userName: userName.value })), 1)
      ]),
      _: 1
    }, 8, ["title"]),
    (monthlyProjects.value?.length)
      ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
          _createVNode(_unref(Multiselect), {
            modelValue: selectedProjectIds.value,
            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((selectedProjectIds).value = $event)),
            options: projectOptions.value,
            placeholder: _unref(t)('budget.filterByProject'),
            mode: "multiple",
            searchable: true,
            "can-clear": true,
            "close-on-select": false,
            class: "project-multiselect"
          }, null, 8, ["modelValue", "options", "placeholder"])
        ]))
      : _createCommentVNode("", true),
    _createElementVNode("div", _hoisted_3, [
      _createElementVNode("div", _hoisted_4, [
        _createVNode(_component_router_link, {
          to: { name: 'My Planning' },
          class: "panel__nav-link"
        }, {
          default: _withCtx(() => [
            _createTextVNode(_toDisplayString(_unref(t)("nav.seeFullPlanning")) + " → ", 1)
          ]),
          _: 1
        })
      ]),
      _createVNode(PlanningCalendar, {
        users: _unref(planning).users.value,
        "visible-days": _unref(planning).visibleDays.value,
        "planned-times": _unref(planning).plannedTimes.value,
        "time-offs": _unref(planning).timeOffs.value,
        holidays: _unref(planning).holidays.value,
        projects: _unref(planning).projects.value,
        "timesheet-entries": _unref(planning).timesheetEntries.value,
        "show-logged-time": true,
        "show-planned-time": true,
        "show-user-column": false,
        "fill-available-height": true,
        "highlight-today": false,
        "current-user-id": user.value?.id,
        "is-admin": isUserAdmin.value,
        onOpenNewPlannedTime: _unref(modals).openNewPlannedTime,
        onOpenNewTimeOff: _unref(modals).openNewTimeOff,
        onOpenEditPlannedTime: _unref(modals).openEditPlannedTime,
        onOpenEditTimeOff: _unref(modals).openEditTimeOff,
        onOpenNewTimesheet: _unref(modals).openNewTimesheet,
        onOpenEditTimesheet: _unref(modals).openEditTimesheet,
        onOpenNewTimesheetSuggestion: _unref(modals).openNewTimesheetFromSuggestion
      }, null, 8, ["users", "visible-days", "planned-times", "time-offs", "holidays", "projects", "timesheet-entries", "current-user-id", "is-admin", "onOpenNewPlannedTime", "onOpenNewTimeOff", "onOpenEditPlannedTime", "onOpenEditTimeOff", "onOpenNewTimesheet", "onOpenEditTimesheet", "onOpenNewTimesheetSuggestion"])
    ]),
    _createElementVNode("div", _hoisted_5, [
      _createElementVNode("div", _hoisted_6, [
        _createVNode(_component_router_link, {
          to: { name: 'ActivityFeed' },
          class: "panel__nav-link"
        }, {
          default: _withCtx(() => [
            _createTextVNode(_toDisplayString(_unref(t)("nav.seeAllActivities")) + " → ", 1)
          ]),
          _: 1
        })
      ]),
      _createVNode(ActivityFeed, {
        "status-filter": ['Today'],
        "hide-filters": true,
        "project-filter": selectedProjectIds.value
      }, null, 8, ["project-filter"])
    ]),
    _createElementVNode("div", _hoisted_7, [
      _createVNode(TileDashboard, {
        title: _unref(t)('projects')
      }, {
        actions: _withCtx(() => [
          _createElementVNode("div", _hoisted_8, [
            _createElementVNode("label", _hoisted_9, [
              _withDirectives(_createElementVNode("input", {
                type: "checkbox",
                "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => (_isRef(showFavoritesOnly) ? (showFavoritesOnly).value = $event : null))
              }, null, 512), [
                [_vModelCheckbox, _unref(showFavoritesOnly)]
              ]),
              _createElementVNode("span", null, _toDisplayString(_unref(t)("budget.onlyFavorites")), 1)
            ]),
            _cache[12] || (_cache[12] = _createElementVNode("span", { class: "budget-toggles__separator" }, null, -1)),
            _createElementVNode("label", _hoisted_10, [
              _withDirectives(_createElementVNode("input", {
                type: "checkbox",
                "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => (_isRef(showTeamBudget) ? (showTeamBudget).value = $event : null))
              }, null, 512), [
                [_vModelCheckbox, _unref(showTeamBudget)]
              ]),
              _createElementVNode("span", null, _toDisplayString(_unref(t)("budget.team")), 1)
            ]),
            _createElementVNode("label", _hoisted_11, [
              _withDirectives(_createElementVNode("input", {
                type: "checkbox",
                "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => (_isRef(showPersonalBudget) ? (showPersonalBudget).value = $event : null))
              }, null, 512), [
                [_vModelCheckbox, _unref(showPersonalBudget)]
              ]),
              _createElementVNode("span", null, _toDisplayString(_unref(t)("budget.personal")), 1)
            ])
          ])
        ]),
        default: _withCtx(() => [
          _createElementVNode("div", {
            class: _normalizeClass(["budget-columns-header", {
            'budget-columns-header--team-only':
              _unref(showTeamBudget) && !_unref(showPersonalBudget),
            'budget-columns-header--personal-only':
              !_unref(showTeamBudget) && _unref(showPersonalBudget),
            'budget-columns-header--both': _unref(showTeamBudget) && _unref(showPersonalBudget),
          }])
          }, [
            _createElementVNode("div", _hoisted_12, [
              _createElementVNode("span", _hoisted_13, _toDisplayString(_unref(t)("budget.project")), 1),
              _createElementVNode("div", _hoisted_14, [
                _createElementVNode("button", {
                  class: _normalizeClass(["budget-order-btn", { 'budget-order-btn--active': nameOrder.value === 'asc' }]),
                  onClick: _cache[4] || (_cache[4] = ($event: any) => (setNameOrder('asc'))),
                  title: _unref(t)('budget.sortAZ')
                }, [
                  _createVNode(_unref(Icon), {
                    icon: "mdi:arrow-up",
                    class: "budget-order-icon"
                  })
                ], 10, _hoisted_15),
                _createElementVNode("button", {
                  class: _normalizeClass(["budget-order-btn", { 'budget-order-btn--active': nameOrder.value === 'desc' }]),
                  onClick: _cache[5] || (_cache[5] = ($event: any) => (setNameOrder('desc'))),
                  title: _unref(t)('budget.sortZA')
                }, [
                  _createVNode(_unref(Icon), {
                    icon: "mdi:arrow-down",
                    class: "budget-order-icon"
                  })
                ], 10, _hoisted_16)
              ])
            ]),
            (_unref(showTeamBudget))
              ? (_openBlock(), _createElementBlock("div", {
                  key: 0,
                  class: _normalizeClass(["budget-column-header", { 'budget-column-header--span': !_unref(showPersonalBudget) }])
                }, [
                  _createElementVNode("span", _hoisted_17, _toDisplayString(_unref(t)("budget.teamConsumption")), 1),
                  _createElementVNode("div", _hoisted_18, [
                    _createElementVNode("button", {
                      class: _normalizeClass(["budget-order-btn", { 'budget-order-btn--active': teamOrder.value === 'asc' }]),
                      onClick: _cache[6] || (_cache[6] = ($event: any) => (setTeamOrder('asc'))),
                      title: _unref(t)('budget.sortAscending')
                    }, [
                      _createVNode(_unref(Icon), {
                        icon: "mdi:arrow-up",
                        class: "budget-order-icon"
                      })
                    ], 10, _hoisted_19),
                    _createElementVNode("button", {
                      class: _normalizeClass(["budget-order-btn", { 'budget-order-btn--active': teamOrder.value === 'desc' }]),
                      onClick: _cache[7] || (_cache[7] = ($event: any) => (setTeamOrder('desc'))),
                      title: _unref(t)('budget.sortDescending')
                    }, [
                      _createVNode(_unref(Icon), {
                        icon: "mdi:arrow-down",
                        class: "budget-order-icon"
                      })
                    ], 10, _hoisted_20)
                  ])
                ], 2))
              : _createCommentVNode("", true),
            (_unref(showPersonalBudget))
              ? (_openBlock(), _createElementBlock("div", {
                  key: 1,
                  class: _normalizeClass(["budget-column-header", { 'budget-column-header--span': !_unref(showTeamBudget) }])
                }, [
                  _createElementVNode("span", _hoisted_21, _toDisplayString(_unref(t)("budget.personalConsumption")), 1),
                  _createElementVNode("div", _hoisted_22, [
                    _createElementVNode("button", {
                      class: _normalizeClass(["budget-order-btn", { 'budget-order-btn--active': personalOrder.value === 'asc' }]),
                      onClick: _cache[8] || (_cache[8] = ($event: any) => (setPersonalOrder('asc'))),
                      title: _unref(t)('budget.sortAscending')
                    }, [
                      _createVNode(_unref(Icon), {
                        icon: "mdi:arrow-up",
                        class: "budget-order-icon"
                      })
                    ], 10, _hoisted_23),
                    _createElementVNode("button", {
                      class: _normalizeClass(["budget-order-btn", {
                  'budget-order-btn--active': personalOrder.value === 'desc',
                }]),
                      onClick: _cache[9] || (_cache[9] = ($event: any) => (setPersonalOrder('desc'))),
                      title: _unref(t)('budget.sortDescending')
                    }, [
                      _createVNode(_unref(Icon), {
                        icon: "mdi:arrow-down",
                        class: "budget-order-icon"
                      })
                    ], 10, _hoisted_24)
                  ])
                ], 2))
              : _createCommentVNode("", true)
          ], 2),
          (user.value?.id)
            ? (_openBlock(), _createElementBlock("ul", _hoisted_25, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(sortedProjects.value, (project) => {
                  return (_openBlock(), _createBlock(ProjectItem, {
                    id: project.id,
                    title: project.name,
                    percentage: project.percentage,
                    "danger-percent": Math.max(1 - 1000 / project.orderedAmount, 0.8),
                    value: _unref(formatPrice)(project.performedAmount),
                    max: _unref(formatPrice)(project.orderedAmount),
                    key: project.id,
                    color: project.color,
                    period: project.period,
                    "user-id": user.value.id,
                    "is-favorite": project.isFavorite,
                    "budget-tracking": 
              project.budgetTracking === _unref(BudgetTracking).MONTHLY
                ? 'monthly'
                : 'global'
            ,
                    "personal-allocated-amount": 
              project.employeeBudgetAllocation?.allocatedAmount
            ,
                    "personal-timesheet-amount": 
              project.employeeBudgetAllocation?.timesheetAmount
            ,
                    "team-percentage": project.percentage,
                    "show-team-budget": _unref(showTeamBudget),
                    "show-personal-budget": _unref(showPersonalBudget),
                    onToggleFavorite: ($event: any) => (toggleFavorite(project.id)),
                    onClick: ($event: any) => (goToProject(project.id))
                  }, null, 8, ["id", "title", "percentage", "danger-percent", "value", "max", "color", "period", "user-id", "is-favorite", "budget-tracking", "personal-allocated-amount", "personal-timesheet-amount", "team-percentage", "show-team-budget", "show-personal-budget", "onToggleFavorite", "onClick"]))
                }), 128))
              ]))
            : _createCommentVNode("", true)
        ]),
        _: 1
      }, 8, ["title"])
    ]),
    (_unref(modals).showModal.value)
      ? (_openBlock(), _createBlock(PlanningModal, {
          key: 1,
          "form-data": _unref(modals).formData.value,
          "onUpdate:formData": _cache[10] || (_cache[10] = ($event: any) => ((_unref(modals).formData.value) = $event)),
          mode: _unref(modals).selectedItemType.value,
          "onUpdate:mode": _cache[11] || (_cache[11] = ($event: any) => ((_unref(modals).selectedItemType.value) = $event)),
          "is-editing": _unref(modals).isEditing.value,
          users: _unref(planning).users.value,
          projects: _unref(planning).projects.value,
          "current-project": _unref(modals).currentProject.value,
          onClose: _unref(modals).closeModal,
          onSave: _unref(modals).savePlanningItem,
          onDelete: _unref(modals).deletePlanningItem
        }, null, 8, ["form-data", "mode", "is-editing", "users", "projects", "current-project", "onClose", "onSave", "onDelete"]))
      : _createCommentVNode("", true),
    (_unref(modals).showTimesheetModal.value && _unref(modals).selectedTimesheet.value)
      ? (_openBlock(), _createBlock(EditTimesheet, {
          key: _unref(modals).timesheetModalKey.value,
          timesheet: _unref(modals).selectedTimesheet.value,
          onClose: _unref(modals).closeTimesheetModal,
          "onUpdate:timesheet": _unref(modals).handleTimesheetUpdated,
          onDelete: _unref(modals).handleTimesheetDeleted
        }, null, 8, ["timesheet", "onClose", "onUpdate:timesheet", "onDelete"]))
      : _createCommentVNode("", true)
  ]))
}
}

})